import { useSession } from '@faststore/sdk'
import { useEffect } from 'react'
import { trackDYLogin } from 'src/sdk/analytics/dynamicYield'
import { BK_ID_STORAGE_KEY } from 'src/sdk/session/validate'

import { DYNAMIC_YIELD_SCRIPT_READY_EVENT } from './DynamicYieldScriptLoader'

const COOKIEBOT_READY = 'CookiebotOnConsentReady'
const COOKIEBOT_ACCEPT = 'CookiebotOnAccept'
const DYNAMIC_YIELD_LOGIN_STORAGE_KEY = 'dynamicYield:login'
const DYNAMIC_YIELD_LOGIN_TEST_CUID_STORAGE_KEY = 'dynamicYield:login:test-cuid'
const DYNAMIC_YIELD_LOGIN_CUID_TYPE = 'customer_id'
const DYNAMIC_YIELD_LOGIN_TTL = 24 * 60 * 60 * 1000

type DynamicYieldLoginStorageValue = {
  cuid: string
  timestamp: number
}

const readDynamicYieldLoginStorage = () => {
  try {
    return JSON.parse(
      window.sessionStorage.getItem(DYNAMIC_YIELD_LOGIN_STORAGE_KEY) ?? 'null'
    ) as DynamicYieldLoginStorageValue | null
  } catch {
    return null
  }
}

const saveDynamicYieldLoginStorage = (cuid: string) => {
  try {
    window.sessionStorage.setItem(
      DYNAMIC_YIELD_LOGIN_STORAGE_KEY,
      JSON.stringify({
        cuid,
        timestamp: Date.now(),
      })
    )
  } catch {
    // Storage non disponibile: l'evento puo' comunque partire, ma senza dedupe.
  }
}

const shouldTrackDynamicYieldLogin = (cuid: string) => {
  const storedValue = readDynamicYieldLoginStorage()

  if (storedValue?.cuid !== cuid) {
    return true
  }

  return Date.now() - (storedValue.timestamp ?? 0) >= DYNAMIC_YIELD_LOGIN_TTL
}

const getDynamicYieldLoginBkId = (personId?: string | null) => {
  if (typeof window === 'undefined' || !personId) {
    return ''
  }

  try {
    const storedCustomer = JSON.parse(
      window.localStorage.getItem(BK_ID_STORAGE_KEY) ?? 'null'
    ) as { customer_id?: string; bk_id?: string | null } | null

    if (storedCustomer?.customer_id !== personId) {
      return ''
    }

    return storedCustomer.bk_id?.trim() ?? ''
  } catch {
    return ''
  }
}

const getDevelopmentDynamicYieldLoginCuid = () => {
  if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') {
    return ''
  }

  try {
    return (
      window.sessionStorage
        .getItem(DYNAMIC_YIELD_LOGIN_TEST_CUID_STORAGE_KEY)
        ?.trim() ?? ''
    )
  } catch {
    return ''
  }
}

const DynamicYieldLoginEventHandler = () => {
  const { person, isValidating } = useSession()
  const personId = person?.id ? String(person.id).trim() : ''
  const bkId = getDynamicYieldLoginBkId(personId)
  const cuid = bkId || personId || getDevelopmentDynamicYieldLoginCuid()

  useEffect(() => {
    if (typeof window === 'undefined' || isValidating || !cuid) {
      return
    }

    const trackLogin = () => {
      if (!shouldTrackDynamicYieldLogin(cuid)) {
        return
      }

      const hasTrackedLogin = trackDYLogin({
        cuid,
        cuidType: DYNAMIC_YIELD_LOGIN_CUID_TYPE,
      })

      if (hasTrackedLogin) {
        saveDynamicYieldLoginStorage(cuid)
      }
    }

    trackLogin()

    window.addEventListener(DYNAMIC_YIELD_SCRIPT_READY_EVENT, trackLogin)
    window.addEventListener(COOKIEBOT_READY, trackLogin)
    window.addEventListener(COOKIEBOT_ACCEPT, trackLogin)

    return () => {
      window.removeEventListener(DYNAMIC_YIELD_SCRIPT_READY_EVENT, trackLogin)
      window.removeEventListener(COOKIEBOT_READY, trackLogin)
      window.removeEventListener(COOKIEBOT_ACCEPT, trackLogin)
    }
  }, [cuid, isValidating])

  return null
}

export default DynamicYieldLoginEventHandler
